<template>
  <div class="layout reference-uniform-items">
    <m-table-settings ref="columnSettings" :columns="columns" @apply="submitTableSettings" />

    <item-sidesheet
      ref="itemModal"
      :can-remove="canRemove"
      @updated="updateItemData"
      @created="updateList"
      @remove="updateList"
    ></item-sidesheet>

    <item-history-sidesheet ref="itemHistoryModal"></item-history-sidesheet>

    <m-issue-item ref="issueItem" @issued="updateItemStatus"></m-issue-item>
    <m-withdraw-item ref="withdrawItem" @withdrawn="updateItemStatus"></m-withdraw-item>
    <m-write-off-item ref="writeOffItem" @writtenOff="updateItemStatus"></m-write-off-item>
    <m-remove-item ref="removeItem" @remove="updateList"></m-remove-item>

    <div class="layout-header">
      <div class="layout-title">Каталог товарно-материальных ценностей</div>
      <div class="layout-header-actions">
        <div class="layout-header-action">
          <c-button2
            size="small"
            label="Структура каталога"
            :icon="{
              icon: 'back',
              color: 'blue',
            }"
            :responsive="true"
            @click="toCatalog"
          />
        </div>

        <div class="layout-header-action">
          <c-button
            :icon="{
              icon: 'google',
              color: 'green',
            }"
            size="small"
            label="Выгрузить в Google таблицы"
            responsive
            @click="toGoogle"
          />
        </div>

        <div class="layout-header-action">
          <c-button
            responsive
            label="Новая ТМЦ"
            :icon="{
              icon: 'plus',
              color: 'white',
            }"
            color="blue"
            @click="addItem"
          />
        </div>
      </div>
    </div>

    <div class="layout-content">
      <c-loader v-if="!inited" :active="!inited" label="Данные загружаются" :height="450"></c-loader>

      <template v-else>
        <filters :filters="filters.fields" @apply="applyFilters" />

        <div class="meta-filters">
          <stats class="stats" :total="stats.totalData" :filtered="stats.filteredData" :loading="loading" />

          <div class="configure-table">
            <div class="configure-table__wrapper">
              <c-button2
                :responsive="true"
                :icon="{ icon: 'filter-drop', color: 'secondary' }"
                label="Сбросить фильтры столбцов"
                size="small"
                :disabled="!hasTableFilters"
                @click="resetTableFilters"
              />

              <c-button2
                :responsive="true"
                size="small"
                label="Настроить столбцы"
                icon="cog"
                type="with-icon"
                @click="openColumnSettingsModal"
              />
            </div>
          </div>
        </div>

        <items-table
          :items="items.list"
          :filters="table.filters"
          :sort-by="table.sortBy"
          :loading="loading"
          :columns="columns"
          :user-columns="userColumns"
          :page="table.page"
          :can-remove="canRemove"
          @sort="sort"
          @filter="applyTableFilters"
          @nextPage="nextPage"
          @edit="editItem"
          @issue="issueItem"
          @withdraw="withdrawItem"
          @writeOff="writeOffItem"
          @restore="restoreItem"
          @remove="removeItem"
          @showHistory="showItemHistory"
        />
      </template>
    </div>

    <top-button root=".layout"></top-button>

    <transition name="fade"><show-items v-if="!loading" :count="items.list.length" :total="totalCount" /></transition>
  </div>
</template>

<script>
import moment from "moment";

import CButton2 from "@/components/controls2/Button";
import CButton from "@/components/controls/button";
import TopButton from "@/components/top-button";
import ShowItems from "@/components/show-items";
import CLoader from "@/components/loader";
import MTableSettings from "@/components/modals/table-columns/modal/";

import { Column } from "@/entities/structures/column";
import ItemHistorySidesheet from "@/components/modals/inventory/ItemHistorySidesheet";
import { userPermissionsList } from "@/constants/userPermissions";
import { param } from "@/utils/url/param";
import { FiltersModel } from "./models/FiltersModel";
import { StatsModel } from "./models/StatsModel";
import { TableModel } from "./models/TableModel";
import { ItemsModel } from "./models/ItemsModel";

import Filters from "./components/Filters";
import Stats from "./components/Stats";
import ItemsTable from "./components/Table";

import ItemSidesheet from "./modals/ItemSidesheet/index";
import MRemoveItem from "./modals/RemoveItemModal";
import MIssueItem from "./modals/IssueItemModal";
import MWithdrawItem from "./modals/WithdrawItemModal";
import MWriteOffItem from "./modals/WriteOffItemModal";

import { ACTION_RESTORE } from "./constants";

const REMOVE_ITEM_PERMISSION = userPermissionsList.referenceBooks.uniformItems.delete;

export default {
  components: {
    CButton,
    CButton2,
    TopButton,
    ShowItems,
    CLoader,
    MTableSettings,

    Filters,
    Stats,
    ItemsTable,

    ItemSidesheet,
    ItemHistorySidesheet,
    MRemoveItem,
    MIssueItem,
    MWithdrawItem,
    MWriteOffItem,
  },

  data() {
    return {
      inited: false,
      loading: false,
      hasMoreItems: true,
      tableName: "inventory",

      canRemove: false,

      totalCount: 0,

      filters: FiltersModel(),
      table: TableModel(),
      stats: StatsModel(),
      items: ItemsModel(),

      columns: [],
      userColumns: [],
      hasTableFilters: false,
    };
  },

  watch: {
    "table.filters": {
      handler(v) {
        this.hasTableFilters = Object.values(v).some((filter) => filter.value);
      },
      deep: true,
    },
  },

  async created() {
    this.currentUser = this.$store.state.userId;

    const userPermissions = this.$store.getters.getUserPermissions;
    this.canRemove = userPermissions.some((permission) => permission === REMOVE_ITEM_PERMISSION);

    await this.loadDictionaries().then(() => {
      const { query } = this.$route;

      return Promise.all([this.filters.init(query), this.table.init(query)]);
    });

    this.inited = true;
    this.load();
  },

  methods: {
    toCatalog() {
      window.open("/index.php?r=redesign#/reference-uniform");
    },
    loadDictionaries() {
      return Promise.all([
        this.$store.dispatch("uniformItems/getColumns").then((response) => {
          this.columns = response && response.length > 0 && response.filter(col => col !== 'expiryDate').map((col) => new Column(col));
        }),
        this.$store.dispatch("uniformItems/getUserColumns").then((response) => {
          this.userColumns = response && response.columns && response.columns.length > 0 && response.columns.filter(col => col !== 'expiryDate');
        }),
        this.$store.dispatch("uniformItems/getCatalog"),
        this.$store.dispatch("actors/getActiveActors"),
      ]);
    },
    getParams() {
      const params = {
        ...this.filters.getParams(),
        ...this.table.getParams(),
      };

      return params;
    },

    async loadItems(params) {
      const { items, currentPage, pageCount, totalCount } = await this.$store.dispatch("uniformItems/getItems", params);
      this.hasMoreItems = currentPage < pageCount;
      this.totalCount = totalCount;

      this.items.add(items);
      console.log('items---->', this.items.list);
      this.table.nextPage();
    },

    async loadStats(params) {
      const data = await this.$store.dispatch("uniformItems/getStats", params);

      this.stats.update(data);
    },

    async load() {
      if (this.loading) return;
      const params = this.getParams();

      this.fixParams();

      this.loading = true;

      await Promise.all([this.loadItems(params), this.loadStats(params)]);

      this.loading = false;
    },

    fixParams() {
      const { query } = this.$route;

      const newQuery = {
        ...this.filters.getUrlParams(),
        ...this.table.getUrlParams(),
      };

      if (JSON.stringify(query) !== JSON.stringify(newQuery)) {
        this.$router.push({ query: newQuery }).catch(() => {});
      }
    },

    resetTable() {
      this.hasMoreItems = true;
      this.items.reset();
      this.table.reset();
      this.stats.reset();
      this.load();
    },

    applyFilters(filters) {
      this.filters.update(filters);
      this.resetTable();
    },

    nextPage() {
      if (this.hasMoreItems) this.load();
    },

    sort(columnName) {
      this.table.sort(columnName);
      this.resetTable();
    },

    applyTableFilters() {
      this.resetTable();
    },

    resetTableFilters() {
      this.table.resetFilters();
      this.resetTable();
    },

    updateList() {
      this.resetTable();
    },

    addItem() {
      this.$refs.itemModal.open();
    },

    editItem(item) {
      this.$refs.itemModal.open(item);
    },

    updateItemData(item) {
      this.items.update(item.id, item);
      this.loadStats(this.getParams());
    },

    updateItemStatus(action) {
      this.items.updateStatus(action.inventory_id, action);
      this.loadStats(this.getParams());
    },

    issueItem(item) {
      this.$refs.issueItem.open(item);
    },

    withdrawItem(item) {
      this.$refs.withdrawItem.open(item);
    },

    writeOffItem(item) {
      this.$refs.writeOffItem.open(item);
    },

    removeItem(item) {
      this.$refs.removeItem.open(item);
    },

    restoreItem(item) {
      this.$store
        .dispatch("uniformItems/action", {
          action: ACTION_RESTORE,
          author_user_id: this.currentUser,
          inventory_id: item.id,
          date: moment().format("DD.MM.YYYY"),
        })
        .then((res) => {
          const nameText = item.name ? `(${item.name})` : "";
          this.$notify({
            group: "success-message",
            data: {
              textMessage: `Позиция №${item.serialNumber} ${nameText} успешно восстановлена`,
            },
          });
          this.updateItemStatus(res.model);
        });
    },

    showItemHistory(item) {
      this.$refs.itemHistoryModal.open(item);
    },

    openColumnSettingsModal() {
      this.$refs.columnSettings.open(this.userColumns);
    },

    submitTableSettings(selectedColumns) {
      this.userColumns = selectedColumns;
      this.$store.dispatch("uniformItems/saveUserColumns", selectedColumns).then(() => {
        this.$notify({
          group: "success-message",
          data: {
            textMessage: "Настройки успешно сохранены",
          },
        });
      });
    },

    toGoogle() {
      const { userId } = this.$store.state;
      const params = this.getParams();

      const queryString = param(params);
      const url = `/index.php?r=google-sheets/create&user_id=${userId}&table=${this.tableName}&${queryString}`;
      console.log({ url });

      window.open(url);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/vars";
.reference-uniform-items {
  .layout-header {
    height: auto;
    min-height: 30px;
  }

  .meta-filters {
    display: flex;
  }

  .stats,
  .configure-table {
    margin-bottom: 20px;
  }

  .configure-table {
    margin-left: auto;
    padding-left: 20px;

    &__wrapper {
      display: flex;
      margin-left: -5px;
      margin-right: -5px;

      /deep/ .c-button {
        margin: 0 5px;
      }
    }
  }

  @include beforeBootstrapXl {
    .meta-filters {
      flex-wrap: wrap;
    }
  }

  @include beforeBootstrapLg {
    .layout-content {
      padding-bottom: 60px;
    }
  }

  @include beforeBootstrapMd {
    .stats {
      margin-left: -30px;
      margin-right: -30px;
      padding-left: 20px;
      padding-right: 20px;
      border-radius: 0;
    }
  }

  @include onlyMobile {
    .stats {
      margin-left: -20px;
      margin-right: -20px;
      padding-left: 10px;
      padding-right: 10px;
    }
  }
}
</style>
